import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { BrandingProvider } from './branding/BrandingProvider';
import { ThemeProvider } from './theme/ThemeProvider';
import { AuthProvider } from './auth/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';

// Each page is its own chunk, fetched when first visited.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminsPage = lazy(() => import('./pages/admins/AdminsPage'));
const FollowupsPage = lazy(() => import('./pages/crm/FollowupsPage'));
const LeadsPage = lazy(() => import('./pages/crm/LeadsPage'));
const LeadDetailPage = lazy(() => import('./pages/crm/LeadDetailPage'));
const StatusesPage = lazy(() => import('./pages/crm/StatusesPage'));
const ChatsPage = lazy(() => import('./pages/whatsapp/ChatsPage'));
const TemplatesPage = lazy(() => import('./pages/whatsapp/TemplatesPage'));
const BotPage = lazy(() => import('./pages/whatsapp/BotPage'));
const CallsPage = lazy(() => import('./pages/crm/CallsPage'));
const DevicesPage = lazy(() => import('./pages/crm/DevicesPage'));
const MyAttendancePage = lazy(() => import('./pages/attendance/MyAttendancePage'));
const StaffAttendancePage = lazy(() => import('./pages/attendance/StaffAttendancePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BranchesPage = lazy(() => import('./pages/BranchesPage'));
const SourcesPage = lazy(() => import('./pages/masters/SourcesPage'));
const WebsiteSettingsPage = lazy(() => import('./pages/masters/WebsiteSettingsPage'));
const MetaSettingsPage = lazy(() => import('./pages/masters/MetaSettingsPage'));
const ComplaintsPage = lazy(() => import('./pages/complaints/ComplaintsPage'));
const ComplaintMastersPage = lazy(() => import('./pages/complaints/ComplaintMastersPage'));
const CustomFieldsPage = lazy(() => import('./pages/crm/CustomFieldsPage'));

export default function App() {
  return (
    <ThemeProvider>
    <BrandingProvider>
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<div className="spinner">Loading…</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route
              path="/admins"
              element={
                <ProtectedRoute permission="admins.view">
                  <AdminsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/leads"
              element={
                <ProtectedRoute anyPermission={['crm.leads.view_own', 'crm.leads.view_branch', 'crm.leads.view_all', 'crm.leads.update_own', 'crm.leads.update_branch', 'crm.leads.update_all']}>
                  <LeadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/leads/:id"
              element={
                <ProtectedRoute anyPermission={['crm.leads.view_own', 'crm.leads.view_branch', 'crm.leads.view_all', 'crm.leads.update_own', 'crm.leads.update_branch', 'crm.leads.update_all']}>
                  <LeadDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/followups"
              element={
                <ProtectedRoute anyPermission={['crm.followups.view_own', 'crm.followups.view_branch', 'crm.followups.view_all', 'crm.followups.create_own', 'crm.followups.create_branch', 'crm.followups.create_all']}>
                  <FollowupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/calls"
              element={
                <ProtectedRoute anyPermission={['crm.calls.view_all', 'crm.calls.view_branch', 'crm.calls.view_own']}>
                  <CallsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/devices"
              element={
                <ProtectedRoute anyPermission={['crm.devices.manage', 'crm.devices.manage_branch']}>
                  <DevicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/statuses"
              element={
                <ProtectedRoute permission="crm.statuses.manage">
                  <StatusesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/masters/website"
              element={
                <ProtectedRoute permission="crm.website.manage">
                  <WebsiteSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/masters/meta"
              element={
                <ProtectedRoute permission="crm.meta.manage">
                  <MetaSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/masters/sources"
              element={
                <ProtectedRoute permission="crm.sources.manage">
                  <SourcesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complaints"
              element={
                <ProtectedRoute anyPermission={['complaints.register', 'complaints.view_branch', 'complaints.view_all', 'complaints.assign_branch', 'complaints.assign_all']}>
                  <ComplaintsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complaints/assigned"
              element={
                <ProtectedRoute permission="complaints.handle">
                  <ComplaintsPage assignedOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="/masters/complaints"
              element={
                <ProtectedRoute permission="complaints.masters">
                  <ComplaintMastersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/custom-fields"
              element={
                <ProtectedRoute permission="crm.custom-fields.manage">
                  <CustomFieldsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance"
              element={
                <ProtectedRoute permission="attendance.own">
                  <MyAttendancePage />
                </ProtectedRoute>
              }
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/:tab" element={<SettingsPage />} />
            <Route
              path="/branches"
              element={
                <ProtectedRoute permission="branches.manage">
                  <BranchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance/staff"
              element={
                <ProtectedRoute anyPermission={['attendance.view_all', 'attendance.manage_all', 'attendance.view_branch', 'attendance.manage_branch']}>
                  <StaffAttendancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/whatsapp/chats"
              element={
                <ProtectedRoute anyPermission={['whatsapp.chats.view_all', 'whatsapp.chats.view_own', 'whatsapp.chats.initiate']}>
                  <ChatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/whatsapp/templates"
              element={
                <ProtectedRoute permission="whatsapp.templates.manage">
                  <TemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/whatsapp/bot"
              element={
                <ProtectedRoute permission="whatsapp.bot.configure">
                  <BotPage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </BrandingProvider>
    </ThemeProvider>
  );
}
